import { Avatar, MenuItem, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { FC, SVGProps, forwardRef } from 'react';
import { Colors } from '../../constants';
import { CheckmarkIcon } from '../../icons';

type DropdownItemProps = {
	label: string;
	value?: string;
	selected?: boolean;
	description?: string;
	leftIcon?: JSX.Element;
};

const DropdownItemBase = forwardRef<HTMLLIElement, DropdownItemProps>(
	({ description, value, label, leftIcon, selected = false, ...props }, ref) => (
		<MenuItem
			{...props}
			ref={ref}
			value={value}
			sx={{
				borderBottom: `1px solid ${Colors.white_a5}`,
				background: Colors.grey_1E1E1E,
				padding: '10px 16px',
				alignItems: 'center',
				gap: '10px',
				width: 'fit-content',
				minWidth: '250px',

				'&:last-of-type': {
					borderBottom: 'none',
				},

				'&:hover': {
					background: `linear-gradient(0deg, ${Colors.white_a5} 0%, ${Colors.white_a5} 100%), ${Colors.grey_1E1E1E}`,
				},
			}}
			disableRipple
		>
			{leftIcon}

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '2px',
					maxWidth: '275px',
					width: '100%',
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'start', gap: '2px', justifyContent: 'space-between' }}>
					<Typography
						sx={{
							color: 'white',
							fontSize: '16px',
							fontFamily: 'Montserrat,sans-serif',
							fontWeight: '700',
							wordWrap: 'break-word',
							whiteSpace: 'wrap',
						}}
					>
						{label}
					</Typography>

					{selected && <CheckmarkIcon width={24} height={24} color="white" />}
				</Box>

				{description && (
					<Typography
						sx={{
							color: Colors.white_a80,
							fontSize: '14px',
							fontFamily: 'Montserrat,sans-serif',
							fontWeight: '500',
							lineHeight: '16.8px',
							wordWrap: 'break-word',
							whiteSpace: 'wrap',
						}}
					>
						{description}
					</Typography>
				)}
			</Box>
		</MenuItem>
	)
);

const DropdownItemAvatar = ({ src }: { src?: string }) => <Avatar src={src} sx={{ width: '24px', height: '24px' }} />;

const DropdownItemIcon = ({ icon: Icon }: { icon: FC<SVGProps<SVGSVGElement>> }) => (
	<Icon width="24px" height="24px" color="white" />
);

const DropdownItemImage = ({ src }: { src?: string }) => (
	<Box
		sx={{
			width: '50px',
			height: '50px',
			borderRadius: '4px',
			backgroundImage: `url(${src})`,
			backgroundSize: 'cover',
			flexShrink: 0,
		}}
	/>
);

DropdownItemBase.displayName = 'DropdownItem';

export const DropdownItem = Object.assign(DropdownItemBase, {
	Avatar: DropdownItemAvatar,
	Icon: DropdownItemIcon,
	Image: DropdownItemImage,
});
